import React, { useEffect, useMemo } from 'react';
import { Button, Drawer, Form, Input, message, Select, Typography } from 'antd';
import { mapRegions, parseJSONArray } from 'utils';
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import {
  CREATE_ERROR,
  EDIT_ERROR,
  REQUEST_LOADING,
  SUCCESS_CREATE,
  SUCCESS_UPDATE,
} from 'constants/alerts';
import { TribesService } from 'services/TribesService';
import { requiredRule } from 'utils/forms/validationRules';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';

function TribesForm({ isActive, initialValues, options, onSuccess, onClose }) {
  const [form] = Form.useForm();
  const isEditing = initialValues !== null;
  const { regions, activities, partners } = options;

  const cities = useMemo(() => {
    return mapRegions(regions);
  }, [options]);

  useEffect(() => {
    if (initialValues) {
      const { title, cities, activities, partner_id } = initialValues; // TODO convert json array to array

      const citiesParsed = parseJSONArray(cities);
      const activitiesParsed = parseJSONArray(activities);

      form.setFieldsValue({
        title,
        cities: citiesParsed,
        activities: activitiesParsed,
        partner_id,
      });
    } else {
      form.resetFields();
    }
  }, [initialValues]);

  const citiesDropdown = (menu) => (
    <>
      {form.getFieldValue('cities')?.length === cities?.length ? (
        <Typography.Link
          onClick={() => form.setFieldsValue({ cities: [] })}
          style={{
            display: 'block',
            padding: '8px',
            width: '100%',
          }}
        >
          <MinusCircleOutlined />
          &nbsp; Отменить выбор
        </Typography.Link>
      ) : (
        <Typography.Link
          onClick={() => {
            form.setFieldsValue({
              cities: cities.map(({ id }) => id),
            });
          }}
          style={{
            display: 'block',
            padding: '8px',
            width: '100%',
          }}
        >
          <PlusCircleOutlined />
          &nbsp; Выбрать все
        </Typography.Link>
      )}
      {menu}
    </>
  );

  const activitiesDropdown = (menu) => (
    <>
      {form.getFieldValue('activities')?.length === activities?.length ? (
        <Typography.Link
          onClick={() => form.setFieldsValue({ activities: [] })}
          style={{
            display: 'block',
            padding: '8px',
            width: '100%',
          }}
        >
          <MinusCircleOutlined />
          &nbsp; Отменить выбор
        </Typography.Link>
      ) : (
        <Typography.Link
          onClick={() => {
            form.setFieldsValue({
              activities: activities.map(({ id }) => id),
            });
          }}
          style={{
            display: 'block',
            padding: '8px',
            width: '100%',
          }}
        >
          <PlusCircleOutlined />
          &nbsp; Выбрать все
        </Typography.Link>
      )}
      {menu}
    </>
  );

  const handleSubmit = (values) => {
    const payload = {
      ...values,
      cities: JSON.stringify(values?.cities),
      activities: JSON.stringify(values?.activities),
    };

    if (isEditing) {
      handleUpdate(payload, initialValues.id);
    } else {
      handleCreate(payload);
    }
  };

  const handleCreate = async (data) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isCreated = await TribesService.createTribes(data);

      if (isCreated) {
        handleConfirm();
        message.success(SUCCESS_CREATE);
      }
    } catch (err) {
      message.error(CREATE_ERROR);
    } finally {
      hide();
    }
  };

  const handleUpdate = async (data, id) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await TribesService.editTribes(data, id);

      if (isUpdated) {
        handleConfirm();
        message.success(SUCCESS_UPDATE);
      }
    } catch (err) {
      message.error(EDIT_ERROR);
    } finally {
      hide();
    }
  };

  const handleConfirm = () => {
    onSuccess();
    onClose();
    form.resetFields();
  };

  return (
    <Drawer
      title={isEditing ? 'Редактирование трайбов' : 'Создание трайбов'}
      width="50%"
      onClose={onClose}
      visible={isActive}
      getContainer={false}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{
          span: LABEL_SPAN,
        }}
        wrapperCol={{ span: CONTENT_SPAN }}
        style={{ width: '100%' }}
        labelAlign="right"
      >
        <Form.Item
          name="title"
          label="Наименование трайба"
          rules={[requiredRule]}
        >
          <Input placeholder="Наименование трайба" allowClear />
        </Form.Item>

        <Form.Item name="cities" label="Город" rules={[requiredRule]}>
          <Select
            allowClear
            showArrow
            showSearch
            mode="multiple"
            optionFilterProp="children"
            maxTagCount="responsive"
            placeholder="Город"
            dropdownRender={citiesDropdown}
          >
            {cities.map(({ id, name }) => (
              <Select.Option key={id} value={id}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="activities"
          label="Вид деятельности"
          rules={[requiredRule]}
        >
          <Select
            allowClear
            showArrow
            showSearch
            mode="multiple"
            optionFilterProp="children"
            maxTagCount="responsive"
            placeholder="Вид деятельности"
            dropdownMatchSelectWidth={200}
            dropdownRender={activitiesDropdown}
          >
            {activities.map(({ id, activity }) => (
              <Select.Option key={id} value={id}>
                {activity}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="partner_id" label="Бизнес партнер">
          <Select
            allowClear
            showArrow
            showSearch
            optionFilterProp="children"
            placeholder="Бизнес партнер"
          >
            {partners.map(({ id, title }) => (
              <Select.Option key={id} value={id}>
                {title}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
          <Button type="primary" htmlType="submit">
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export { TribesForm };
